'use client';
import { useRouter, usePathname } from 'next/navigation';
import { Home, Search, Menu, Building2, Megaphone, Inbox, CheckSquare, BarChart3, Settings, Bell, Plus, TrendingUp, Phone, PhoneCall, Zap, LogOut, Wifi, WifiOff, UserCog, Linkedin } from 'lucide-react';
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { usePermission } from '@/components/PermissionProvider';
import { notifyAuthChange } from '@/lib/authEvents';

export default function Sidebar() {
    const navigate = useRouter();
    const pathname = usePathname();
    const active = pathname; // Auto-detect active route from pathname
    const [user, setUser] = useState<{ name: string; email: string; role?: string } | null>(null);

    const { hasAdminPermission, isLoading } = usePermission();

    useEffect(() => {
        // Get user data from localStorage
        const userData = localStorage.getItem('user');
        if (userData) {
            try {
                const parsedUser = JSON.parse(userData);
                setUser(parsedUser);
            } catch (error) {
                console.error('Error parsing user data:', error);
            } 
        }
    }, []);

    const handleLogout = () => {
        // Clear localStorage
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        notifyAuthChange();

        // Show success message
        toast.success('Logged out successfully');

        // Redirect to login page
        navigate.push('/login');
    };

    const menuItems: Array<{ icon: any; label: string; path: string; badge?: string }> = [
        { icon: Home, label: 'Home', path: '/' },
        { icon: Zap, label: 'Leads', path: '/leads', },
        { icon: Search, label: 'Search', path: '/search' },
        // { icon: Users, label: 'Prospects', path: '/prospects' },
        { icon: Building2, label: 'Companies', path: '/accounts' },
        { icon: Megaphone, label: 'Campaigns', path: '/campaigns' },
        { icon: Linkedin, label: 'Accounts', path: '/linkedin-accounts' },
        // { icon: Phone, label: 'Dialer', path: '/dialer' },
        { icon: PhoneCall, label: 'Call Logs', path: '/call-logs' },
        { icon: Inbox, label: 'Inbox', path: '/inbox', },
        { icon: CheckSquare, label: 'Tasks', path: '/tasks', },
        // { icon: BarChart3, label: 'Reports', path: '/reports' },
        { icon: Settings, label: 'Settings', path: '/settings' },
        // Conditionally add Assign Role menu item if user has admin permission
        ...(hasAdminPermission ? [{ icon: UserCog, label: 'Assign Role', path: '/assign-role' }] : []),
    ];

    return (
        <div className="flex flex-col bg-white border-r w-60 h-screen overflow-y-auto">
            <div className="p-[18px] border-b">
                <div className="flex items-center gap-2">
                    <div className="flex justify-center items-center bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg w-8 h-8">
                        <TrendingUp className="w-5 h-5 text-white" />
                    </div>
                    <div className='flex flex-row items-center gap-2'>  
                          <h1 className="font-bold text-xl">SalesFlow</h1> 
                            {/* <Menu /> */}
                    </div>
                </div>
            </div> 
            
            <nav className="flex-1 space-y-1 p-4">
                {menuItems?.map((item) => {
                    const Icon = item.icon;
                    const isActive = active === item.path;
                    return (
                        <button
                            key={item.path}
                            onClick={() => navigate.push(item.path)}
                            className={`w-full whitespace-nowrap flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${isActive ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-50'
                                }`}
                            data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                        >
                            <Icon className="w-5 h-5" />
                            <span className="flex-1 font-medium text-left">{item.label}</span>
                            {item.badge && (
                                <span className="bg-2463EB px-2 py-1 rounded-full font-semibold text-white text-xs">
                                    {item.badge}
                                </span>
                            )}
                        </button>
                    );
                })}
            </nav>
            <div className="p-4 border-t">
                <div className="flex items-center gap-3 mb-3">
                    <div className="flex justify-center items-center bg-gradient-to-br from-blue-400 to-indigo-500 rounded-full w-10 h-10 font-semibold text-white">
                        {user?.name ? user.name.split(' ').map(n => n[0]).join('').toUpperCase() : 'U'}
                    </div>
                    <div className="flex-1">
                        <p className="font-semibold text-sm">{user?.name || 'User'}</p>
                        <p className="text-gray-600 text-xs">{user?.role || user?.email || 'Sales Rep'}</p>
                    </div>
                </div>
                <button
                    onClick={handleLogout}
                    className="flex items-center gap-3 hover:bg-red-50 px-4 py-2 rounded-lg w-full text-red-600 transition-colors"
                >
                    <LogOut className="w-4 h-4" />
                    <span className="font-medium text-sm">Logout</span>
                </button>
            </div>
        </div>
    );
}

